/**
 * LevNutri - Painel Administrativo
 * Acesso restrito a usuários com role = 'admin'
 */

import React, { useState, useEffect, useCallback } from 'react';
import { adminService, usersService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import ConfirmModal from '../Modal/ConfirmModal';
import { Ico, emo } from '../ui/Ico';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ background: '#ffffff', borderRadius: '12px', padding: '1.25rem 1.5rem', boxShadow: '0 2px 8px rgba(44,44,44,0.07)', borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: '1.6rem', marginBottom: '0.25rem' }}>{emo(icon)}</div>
      <div style={{ fontSize: '2rem', fontWeight: 600, color: '#2f2a26', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.82rem', color: '#736b64', marginTop: '0.25rem' }}>{emo(label)}</div>
    </div>
  );
}

function RoleBadge({ role }) {
  const style = role === 'admin'
    ? { background: '#7189a6', color: '#ffffff' }
    : { background: '#edf1f6', color: '#4f6a86' };
  return (
    <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '20px', fontWeight: 600, ...style }}>
      {role === 'admin' ? 'Admin' : 'Nutricionista'}
    </span>
  );
}

function ActiveBadge({ active }) {
  return (
    <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '20px', fontWeight: 600,
      background: active == 1 ? '#edf4ee' : '#f8ebe9', color: active == 1 ? '#46714f' : '#a8483f' }}>
      {active == 1 ? 'Ativa' : 'Inativa'}
    </span>
  );
}

// ─── Modal de Usuário ─────────────────────────────────────────────────────────

function UserModal({ user, onClose, onSaved }) {
  const isEdit = !!user?.id;
  const [form, setForm] = useState({
    name:         user?.name         || '',
    name_display: user?.name_display || '',
    email:        user?.email        || '',
    crn:          user?.crn          || '',
    role:         user?.role         || 'nutricionista',
    active:       user?.active != null ? user.active : 1,
    password:     '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const res = isEdit
        ? await adminService.updateUser(user.id, payload)
        : await adminService.createUser(payload);
      if (res.success) { onSaved(); onClose(); }
      else setError(res.error || 'Erro ao salvar');
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>{isEdit ? 'Editar Usuária' : 'Nova Usuária'}</h2>
          <button onClick={onClose} style={closeBtn}><Ico e="✕" /></button>
        </div>

        {error && <div style={S.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={S.grid2}>
            <div style={S.field}>
              <label style={S.label}>Nome completo *</label>
              <input name="name" required style={S.input} value={form.name} onChange={handleChange} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Nome de exibição</label>
              <input name="name_display" style={S.input} placeholder="Como aparece no sistema" value={form.name_display} onChange={handleChange} />
            </div>
            <div style={S.field}>
              <label style={S.label}>E-mail *</label>
              <input name="email" type="email" required style={S.input} value={form.email} onChange={handleChange} />
            </div>
            <div style={S.field}>
              <label style={S.label}>CRN</label>
              <input name="crn" style={S.input} placeholder="Ex: CRN-3 12345" value={form.crn} onChange={handleChange} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Perfil</label>
              <select name="role" style={S.input} value={form.role} onChange={handleChange}>
                <option value="nutricionista">Nutricionista</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Status</label>
              <select name="active" style={S.input} value={form.active} onChange={handleChange}>
                <option value={1}>Ativa</option>
                <option value={0}>Inativa</option>
              </select>
            </div>
            <div style={{ ...S.field, gridColumn: '1 / -1' }}>
              <label style={S.label}>{isEdit ? 'Nova senha (deixe em branco para manter)' : 'Senha *'}</label>
              <input name="password" type="password" required={!isEdit} style={S.input} value={form.password} onChange={handleChange} autoComplete="new-password" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
            <button type="button" style={S.secondaryBtn} onClick={onClose}>Cancelar</button>
            <button type="submit" style={S.primaryBtn} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal de Transferência ───────────────────────────────────────────────────

function TransferModal({ patient, users, onClose, onSaved }) {
  const [targetId, setTargetId] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const handleSave = async () => {
    if (!targetId) { setError('Selecione uma nutricionista'); return; }
    setSaving(true); setError('');
    try {
      const res = await adminService.transferPatient(patient.id, targetId);
      if (res.success) { onSaved(); onClose(); }
      else setError(res.error || 'Erro ao transferir');
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao transferir');
    } finally { setSaving(false); }
  };

  const nutris = users.filter(u => u.role === 'nutricionista' && u.active == 1 && String(u.id) !== String(patient.nutricionista_id));

  return (
    <div style={overlay}>
      <div style={{ ...modal, maxWidth: '420px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Transferir Paciente</h2>
          <button onClick={onClose} style={closeBtn}><Ico e="✕" /></button>
        </div>
        <p style={{ color: '#4a4440', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Transferir <strong>{patient.name}</strong> para:
        </p>
        {error && <div style={S.error}>{error}</div>}
        <select style={{ ...S.input, marginBottom: '1.25rem' }} value={targetId} onChange={e => setTargetId(e.target.value)}>
          <option value="">Selecione a nutricionista destino...</option>
          {nutris.map(u => <option key={u.id} value={u.id}>{u.name_display || u.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button style={S.secondaryBtn} onClick={onClose}>Cancelar</button>
          <button style={S.primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Transferindo...' : 'Transferir'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Aba Dashboard ────────────────────────────────────────────────────────────

function TabDashboard({ stats }) {
  if (!stats) return <p style={S.emptyMsg}>Carregando...</p>;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <StatCard icon="👩‍⚕️" label="Nutricionistas"     value={stats.nutricionistas}        color="#D4A574" />
        <StatCard icon="✅" label="Ativas"               value={stats.nutricionistas_ativas}  color="#46714f" />
        <StatCard icon="👥" label="Pacientes"             value={stats.pacientes}              color="#7189a6" />
        <StatCard icon="🥗" label="Planos Gerados"        value={stats.planos}                 color="#e8c9a8" />
        <StatCard icon="⚖️" label="Bioimpedâncias"       value={stats.bioimpedancias}         color="#b8c7d8" />
        <StatCard icon="📅" label="Novos Pacientes (mês)" value={stats.pacientes_mes}          color="#b9d2bd" />
      </div>

      {stats.top_nutricionistas?.length > 0 && (
        <div style={S.card}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#2f2a26', margin: '0 0 1rem' }}>Top Nutricionistas por Pacientes</h3>
          <table style={S.table}>
            <thead>
              <tr>
                {['Nutricionista','CRN','Perfil','Status','Pacientes'].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {stats.top_nutricionistas.map(u => (
                <tr key={u.id}>
                  <td style={S.td}>{u.name_display || u.name}</td>
                  <td style={S.td}>{u.crn || '-'}</td>
                  <td style={S.td}><RoleBadge role={u.role || 'nutricionista'} /></td>
                  <td style={S.td}><ActiveBadge active={u.active} /></td>
                  <td style={S.td}><strong>{u.total_patients}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Aba Pacientes ────────────────────────────────────────────────────────────

function TabPatients({ users }) {
  const { showError } = useToast();
  const [patients, setPatients]     = useState([]);
  const [total, setTotal]           = useState(0);
  const [search, setSearch]         = useState('');
  const [nutriFilter, setNutriFilter] = useState('');
  const [loading, setLoading]       = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [confirmDelete, setConfirmDelete]   = useState(null); // { id, name }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getAllPatients(search, nutriFilter || null);
      if (res.success) { setPatients(res.data.patients); setTotal(res.data.total); }
    } catch {} finally { setLoading(false); }
  }, [search, nutriFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      const res = await adminService.deletePatient(confirmDelete.id);
      if (res.success) load();
    } catch (e) { showError(e.response?.data?.error || 'Erro ao remover'); }
    finally { setConfirmDelete(null); }
  };

  const nutris = users.filter(u => u.role === 'nutricionista');

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...S.input, maxWidth: '280px' }} placeholder="Buscar por nome, e-mail ou telefone..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...S.input, maxWidth: '220px' }} value={nutriFilter} onChange={e => setNutriFilter(e.target.value)}>
          <option value="">Todas as nutricionistas</option>
          {nutris.map(u => <option key={u.id} value={u.id}>{u.name_display || u.name}</option>)}
        </select>
        <span style={{ color: '#736b64', fontSize: '0.85rem' }}>{total} resultado{total !== 1 ? 's' : ''}</span>
      </div>

      {transferTarget && (
        <TransferModal
          patient={transferTarget}
          users={users}
          onClose={() => setTransferTarget(null)}
          onSaved={() => { setTransferTarget(null); load(); }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Remover paciente"
          message={`Remover "${confirmDelete.name}"? Esta ação é irreversível e apagará todos os dados do paciente.`}
          confirmLabel="Remover"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      <div style={S.card}>
        {loading ? <p style={S.emptyMsg}>Carregando...</p> : patients.length === 0 ? (
          <p style={S.emptyMsg}>Nenhum paciente encontrado.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>{['Nome','E-mail','Sexo','Cadastro','Nutricionista',''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {patients.map(p => (
                  <tr key={p.id}>
                    <td style={S.td}>{p.name}</td>
                    <td style={S.td}>{p.email || '-'}</td>
                    <td style={S.td}>{p.gender || '-'}</td>
                    <td style={S.td}>{fmtDate(p.created_at)}</td>
                    <td style={S.td}>{p.nutricionista_nome || '-'}</td>
                    <td style={S.td}>
                      <button style={{ ...S.iconBtn, fontSize: '0.82rem', marginRight: '0.5rem', color: '#4f6a86' }}
                        onClick={() => setTransferTarget(p)}><Ico e="🔄" sp />Transferir</button>
                      <button style={{ ...S.iconBtn, color: '#a8483f' }}
                        onClick={() => setConfirmDelete({ id: p.id, name: p.name })}><Ico e="🗑" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Aba Nutricionistas ───────────────────────────────────────────────────────

function TabUsers({ users, onRefresh }) {
  const [editUser, setEditUser]     = useState(null);
  const [showModal, setShowModal]   = useState(false);
  const [error, setError]           = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // user object

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      const res = await adminService.deleteUser(confirmDelete.id);
      if (res.success) onRefresh();
      else setError(res.error || 'Erro ao remover');
    } catch (e) { setError(e.response?.data?.error || 'Erro ao remover'); }
    finally { setConfirmDelete(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button style={S.primaryBtn} onClick={() => { setEditUser(null); setShowModal(true); }}>+ Nova Usuária</button>
      </div>
      {error && <div style={S.error}>{error}</div>}

      {showModal && (
        <UserModal
          user={editUser}
          onClose={() => setShowModal(false)}
          onSaved={onRefresh}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Remover usuária"
          message={`Remover "${confirmDelete.name}"? Esta ação é irreversível.`}
          confirmLabel="Remover"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      <div style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{['Nome','E-mail','CRN','Perfil','Status','Pacientes','Desde',''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={S.td}>
                    <strong>{u.name_display || u.name}</strong>
                    {u.name_display && u.name !== u.name_display && <span style={{ display: 'block', fontSize: '0.75rem', color: '#736b64' }}>{u.name}</span>}
                  </td>
                  <td style={S.td}>{u.email}</td>
                  <td style={S.td}>{u.crn || '-'}</td>
                  <td style={S.td}><RoleBadge role={u.role} /></td>
                  <td style={S.td}><ActiveBadge active={u.active} /></td>
                  <td style={S.td}>{u.total_patients}</td>
                  <td style={S.td}>{fmtDate(u.created_at)}</td>
                  <td style={S.td}>
                    <button style={S.iconBtn} onClick={() => { setEditUser(u); setShowModal(true); }}><Ico e="✏" /></button>
                    <button style={S.iconBtn} onClick={() => setConfirmDelete(u)}><Ico e="🗑" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────

export default function Admin({ onNavigate }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats]         = useState(null);
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        adminService.getStats(),
        adminService.getAllUsers(),
      ]);
      if (statsRes.success) setStats(statsRes.data);
      if (usersRes.success) setUsers(usersRes.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div style={S.page}>
      {/* Cabeçalho */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}><Ico e="⚙" sp />Painel Administrativo</h1>
          <p style={S.subtitle}>Gestão completa do sistema LevNutri</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {emo([
          { key: 'dashboard',      label: '📊 Dashboard' },
          { key: 'patients',       label: '👥 Pacientes' },
          { key: 'nutricionistas', label: '👩‍⚕️ Nutricionistas' },
        ].map(tab => (
          <button key={tab.key}
            style={{ ...S.tab, ...(activeTab === tab.key ? S.tabActive : {}) }}
            onClick={() => setActiveTab(tab.key)}>
            {emo(tab.label)}
          </button>
        )))}
      </div>

      {loading && activeTab === 'dashboard' ? (
        <p style={S.emptyMsg}>Carregando...</p>
      ) : (
        <>
          {activeTab === 'dashboard'      && <TabDashboard stats={stats} />}
          {activeTab === 'patients'       && <TabPatients users={users} />}
          {activeTab === 'nutricionistas' && <TabUsers users={users} onRefresh={loadAll} />}
        </>
      )}
    </div>
  );
}

// ─── Estilos ──────────────────────────────────────────────────────────────────

const S = {
  page:        { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  title:       { fontSize: '1.75rem', fontWeight: 600, color: '#2f2a26', margin: '0 0 0.25rem' },
  subtitle:    { color: '#736b64', fontSize: '0.9rem', margin: 0 },
  card:        { background: '#ffffff', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(44,44,44,0.07)' },
  label:       { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a4440', marginBottom: '0.4rem' },
  input:       { width: '100%', padding: '0.65rem 1rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.95rem', color: '#2f2a26', boxSizing: 'border-box', outline: 'none' },
  grid2:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: '1rem', marginBottom: '0.5rem' },
  field:       { display: 'flex', flexDirection: 'column' },
  primaryBtn:  { background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '0.7rem 1.75rem', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
  secondaryBtn:{ background: '#f6f0ea', color: '#4a4440', border: 'none', borderRadius: '8px', padding: '0.7rem 1.5rem', fontSize: '0.95rem', cursor: 'pointer' },
  tabs:        { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  tab:         { padding: '0.6rem 1.25rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', background: '#ffffff', color: '#736b64', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 },
  tabActive:   { background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', borderColor: 'transparent' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' },
  th:          { padding: '0.75rem', textAlign: 'left', background: '#fbf8f5', borderBottom: '2px solid #d9d0c6', fontWeight: 600, color: '#4a4440', whiteSpace: 'nowrap' },
  td:          { padding: '0.65rem 0.75rem', borderBottom: '1px solid #ebe3da', color: '#2f2a26' },
  iconBtn:     { background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', marginRight: '0.25rem' },
  emptyMsg:    { color: '#736b64', textAlign: 'center', padding: '2rem 0' },
  error:       { background: '#f8ebe9', color: '#a8483f', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' },
};

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
};
const modal = {
  background: '#ffffff', borderRadius: '16px', padding: '2rem', width: '100%',
  maxWidth: '600px', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto',
};
const closeBtn = {
  background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#736b64', lineHeight: 1,
};
